import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Award, Flame, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PointsModal = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [pointsData, setPointsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchPoints();
    }
  }, [isOpen, isAuthenticated]);

  const fetchPoints = async () => {
    try {
      const response = await axios.get(`${API}/points/summary`);
      setPointsData(response.data);
    } catch (error) {
      console.error('Error fetching points:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      spark: 'text-orange-400',
      depth: 'text-orange-500',
      breakthrough: 'text-orange-600'
    };
    return colors[level] || 'text-orange-primary';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md max-h-[85vh] overflow-hidden rounded-2xl shadow-2xl relative flex flex-col"
          style={{ backgroundColor: '#FFF8F0' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="points-modal"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-charcoal/5 transition-colors z-10"
            data-testid="close-points-modal"
          >
            <X size={20} className="text-charcoal/60" />
          </button>

          {/* Header */}
          <div className="p-8 pb-4">
            <h2 className="font-serif text-3xl text-charcoal mb-1">Your Points</h2>
            <p className="text-charcoal/60 text-sm">Track your conversation journey</p>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
            </div>
          ) : pointsData ? (
            <div className="flex-1 overflow-y-auto">
              {/* Points summary */}
              <div className="px-8 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-serif text-charcoal">{pointsData.total_points}</span>
                      <span className="text-charcoal/50 text-sm">points</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: 'rgba(255, 120, 10, 0.15)',
                          color: '#ff780a'
                        }}
                      >
                        Level {pointsData.level}
                      </span>
                      <span className="text-charcoal/70 text-sm">{pointsData.level_title}</span>
                    </div>
                  </div>
                  
                  {/* Streak */}
                  {pointsData.streak_days > 0 && (
                    <div className="flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-primary/10">
                      <Flame size={18} className="text-orange-primary" />
                      <span className="text-charcoal font-medium">{pointsData.streak_days}</span>
                      <span className="text-charcoal/60 text-xs">day streak</span>
                    </div>
                  )}
                </div>

                {/* Progress to next level */}
                <div className="mb-2">
                  <div className="flex justify-between text-xs text-charcoal/50 mb-1">
                    <span>{pointsData.total_points} pts</span>
                    <span>{pointsData.next_level_points} pts</span>
                  </div>
                  <div className="h-2 rounded-full bg-charcoal/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ 
                        width: `${Math.min(100, (pointsData.total_points / pointsData.next_level_points) * 100)}%` 
                      }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: '#ff780a' }}
                    />
                  </div>
                </div>
              </div>

              {/* Past thoughts */}
              <div className="border-t border-charcoal/10 px-8 py-4">
                <h3 className="text-sm font-medium text-charcoal/60 mb-3">Past Thoughts</h3>
                
                {pointsData.completed_thoughts.length > 0 ? (
                  <div className="space-y-3">
                    {pointsData.completed_thoughts.map((thought, index) => (
                      <motion.div
                        key={thought.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start gap-3 py-2"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-charcoal text-sm line-clamp-2 leading-relaxed">
                            {thought.thought_content}
                          </p>
                          <p className="text-charcoal/40 text-xs mt-1">
                            {formatDate(thought.completed_at)}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className={`text-sm font-medium ${getLevelColor(thought.thought_level)}`}>
                            +{thought.points_earned}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-charcoal/40 text-sm text-center py-4">
                    No thoughts completed yet. Start talking!
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <p className="text-charcoal/60">Unable to load points</p>
            </div>
          )}

          {/* Point values legend */}
          <div className="border-t border-charcoal/10 p-4 bg-white/50">
            <div className="flex justify-center gap-6 text-xs text-charcoal/50">
              <span><span className="text-orange-400 font-medium">Spark</span> +10</span>
              <span><span className="text-orange-500 font-medium">Depth</span> +25</span>
              <span><span className="text-orange-600 font-medium">Breakthrough</span> +50</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PointsModal;
